import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { awards, awardsSince } from '../data/landing';
import { AwardsPulse } from './AwardsPulse';
type Award = (typeof awards)[number];

// Only the most recent highlights are listed; the totals still count every award
const SHOWN = 9;

// Group awards by year, newest first (data is already ordered)
const byYear = awards.slice(0, SHOWN).reduce<{
  year: string;
  items: Award[];
}[]>((groups, a) => {
  const last = groups[groups.length - 1];
  if (last && last.year === a.year) last.items.push(a);else groups.push({
    year: a.year,
    items: [a]
  });
  return groups;
}, []);
const bodies = new Set(awards.map(a => a.from)).size;

/**
 * Two-column awards: a sticky serif statement with two quiet figures on the left,
 * and a year-by-year ledger on the right. No floating previews — just type and rules.
 */
export function Awards() {
  return <section id="awards" aria-labelledby="awards-heading" className="relative isolate bg-bg px-page pb-20 md:pb-28" data-id="element-18" data-mp="1r9z23pqq5fi8">
      {/* Clipping lives on this wrapper, not the section — overflow on the section would break the sticky statement */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden="true" data-id="element-19" data-mp="26zm0ein4w6kr">
        <AwardsPulse data-id="element-20" data-mp="2bs6ttj24ohnr" />
      </div>
      <div className="relative grid gap-14 md:grid-cols-12 md:gap-x-6" data-id="element-21" data-mp="2btzrw9yodtk5">
        {/* Statement */}
        <div className="md:sticky md:top-28 md:col-span-5 md:self-start" data-id="element-22" data-mp="2gskq63uda35y">
          <h2 id="awards-heading" className="font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] text-fg md:text-[48px] [text-wrap:balance]" data-id="element-23" data-mp="1k2aewr85vxnp">
            Recognised for craft by our <span className="text-fg/40" data-id="element-24" data-mp="1t3ib93r4aof5">peers</span> since {awardsSince}.
          </h2>

          <dl className="mt-12 grid max-w-[360px] grid-cols-2 gap-6" data-id="element-25" data-mp="1xy5rkfbywcin">
            <div className="flex flex-col-reverse gap-2" data-id="element-26" data-mp="34fj7f3qb9f4x">
              <dt className="text-[13px] text-fg/50" data-id="element-27" data-mp="39f5knd652vdq">Awards and mentions</dt>
              <dd className="text-[40px] font-normal leading-none tabular-nums tracking-[-0.03em] text-fg" data-id="element-28" data-mp="39h8igjdj9st0">
                {awards.length}
              </dd>
            </div>
            <div className="flex flex-col-reverse gap-2" data-id="element-29" data-mp="2erx89ii6cf32">
              <dt className="text-[13px] text-fg/50" data-id="element-30" data-mp="2tpplg4090re3">Award bodies</dt>
              <dd className="text-[40px] font-normal leading-none tabular-nums tracking-[-0.03em] text-fg" data-id="element-31" data-mp="2oqcylj9dnkbi">{bodies}</dd>
            </div>
          </dl>
        </div>

        {/* Year-by-year ledger */}
        <ol className="md:col-span-7 md:col-start-6" aria-label="Awards by year" data-id="element-32" data-mp="1w1pbrj8zcmvu">
          {byYear.map(group => <li key={group.year} className="grid grid-cols-[64px_1fr] border-t border-line py-4 md:grid-cols-[96px_1fr] md:py-5" data-id="element-33" data-mp="2ar3lwtkdr8ca">
              <span className="text-[14px] tabular-nums text-fg/45" data-id="element-34" data-mp="25z3g4khmpl7s">{group.year}</span>
              <ul className="flex flex-col gap-3" data-id="element-35" data-mp="1i993o4181yo2">
                {group.items.map(a => <li key={`${a.award}-${a.from}-${a.project}`} data-id="element-36" data-mp="1da4v9fm4g4ys">
                    <a href="#work" className="group -mx-3 -my-1.5 flex items-start justify-between gap-6 rounded-[6px] px-3 py-1.5 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-fg/[0.04] focus:outline-none focus-visible:bg-fg/[0.04] focus-visible:ring-2 focus-visible:ring-fg" data-id="element-37" data-mp="3oyt6wcl92amc">
                      <span data-id="element-38" data-mp="3jxdnsq2mue6l">
                        <span className="block text-[16px] leading-[1.3] tracking-[-0.01em] text-fg/80 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:text-fg group-focus-visible:text-fg md:text-[17px]" data-id="element-39" data-mp="bjwlqeeuouxu">
                          {a.award}
                        </span>
                        <span className="mt-0.5 block text-[13px] text-fg/45 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:text-fg/60 group-focus-visible:text-fg/60" data-id="element-40" data-mp="6kaa29eo7mr5">
                          {a.from} · {a.project}
                        </span>
                      </span>
                      <ArrowUpRightIcon className="mt-1.5 h-4 w-4 shrink-0 text-fg/30 transition-[transform,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-fg" aria-hidden="true" data-id="element-41" data-mp="37ifk8gxlb6yy" />
                    </a>
                  </li>)}
              </ul>
            </li>)}
          <li className="border-t border-line" aria-hidden="true" data-id="element-42" data-mp="3qg9q4m3f92d4" />
        </ol>
      </div>
    </section>;
}